import React, { useState } from 'react';
import { usePageMeta } from '../Hooks';
import './Pages.css';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface SubmissionState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  message?: string;
}

const Contact: React.FC = () => {
  usePageMeta(
    'Contact Us | UK Pension vs Index Fund Calculator',
    'Get in touch with questions or feedback about our UK pension and index fund calculator.'
  );

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [submission, setSubmission] = useState<SubmissionState>({ status: 'idle' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmission({ status: 'submitting' });

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const cleanBaseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
      const response = await fetch(`${cleanBaseUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok || body?.error) {
        const reason = body?.error ?? 'Failed to send message. Please try again later.';
        setSubmission({ status: 'error', message: reason });
        return;
      }

      setSubmission({
        status: 'success',
        message: 'Thank you for your message! We\'ll get back to you soon.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmission({ status: 'error', message: 'Could not reach the server. Please check your connection and try again.' });
    }
  };

  return (
    <div className="page-container">
      <h1>Contact Us</h1>
      <div className="page-content">
        <p>
          Have questions, feedback, or suggestions about our Pension vs Index Fund Calculator? 
          We'd love to hear from you!
        </p>

        <div className="contact-methods">
          <h2>Get In Touch</h2>
          <p>
            Have questions, feedback, or suggestions? Please use the contact form below and we'll get back to you as soon as possible.
          </p>
        </div>

        {submission.status === 'success' && (
          <div className="submission-message success">
            {submission.message}
          </div>
        )}

        {submission.status === 'error' && (
          <div className="submission-message error">
            {submission.message}
          </div>
        )}

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={6}
              required
            />
          </div>

          <button 
            type="submit" 
            className="submit-button" 
            disabled={submission.status === 'submitting'}
          >
            {submission.status === 'submitting' ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        <div className="response-info">
          <h3>Response Time</h3>
          <p>
            We typically respond to inquiries within 1-2 business days. For urgent matters, 
            please mention "URGENT" in your subject line.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;